import { Button } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import { useHttp } from "../../hooks/useHttp";
import { useUserPermissions } from "~context/UserPermissionsContext";
import PermissionCheck from "../PermissionCheck";
import ImageUploader from "./ImageUploader";
import { Loader } from "./index";
import { useNotification } from "~hooks/useNotification";

const UploadForm = ({
                      imageFor,
                      imageType,
                      dataId = '',
                      image,
                      refetch,
                      addModalMode = false,
                      handleChangeFile = undefined,
                      modalLoading = false,
                      className = '',
                      uploadUrl = '',
                      permissionKey
                    }) => {
  /** it is non obvious, but `base64file` could be base64 or just a regular url (http://...) */
  const [base64file, setBase64File] = useState(image?.url && `${image.url}?size=RAW`);
  const [file, setFile] = useState();
  const { sport, organizationId } = useGetOrgData();
  const { loading, error, request, cleanError } = useHttp();

  const notification = useNotification();

  useEffect(() => {
    if (!file) {
      setBase64File(image?.url && `${image.url}?size=RAW`);
    }
  }, [image, file]);

  // reset internal state when parent clears its own image state
  useEffect(() => {
    if (!image || !(image.url || image.file)) {
      setFile(undefined);
      setBase64File(undefined);
    }
  }, [image]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: "File upload failed",
        description: error,
        placement: "bottomRight",
      });
      cleanError();
    }
  }, [cleanError, error]);

  const handleClear = useCallback(async () => {
    handleChangeFile?.(imageType, file);

    if (!file && image && !addModalMode) {
      const endpoint = `/v1/${sport}/o/${organizationId}/images/${image.imageId}`;
      try {
        await request({ url: endpoint, method: "DELETE" });
        notification.info({
          message: "Image clear successfully",
          description: error,
          placement: "bottomRight",
        });
      } catch (e) {
        console.error(e);
      }
      setFile(null);
      setBase64File(null);
      if (refetch) {
        refetch();
      }
    } else {
      setFile(null);
      if (image?.url) {
        setBase64File(`${image.url}?size=RAW`);
      }
    }
  }, [imageType, file, image, refetch, error, organizationId, request, sport, handleChangeFile, addModalMode]);

  const handleDrop = useCallback(
    (files) => {
      if (files?.[0]) {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
          setBase64File(reader.result);
          setFile(files[0]);
          if (handleChangeFile) {
            handleChangeFile(imageType, {
              file: files[0],
              url: reader.result,
            });
          }
        };
        reader.onerror = (error) => {
          console.error("Error: ", error);
        };
      }
    },
    [handleChangeFile, imageType],
  );

  const handleLoad = useCallback(async () => {
    if (file) {
      const endpoint = uploadUrl || `/v1/${sport}/o/${organizationId}/images/for/${imageFor}/${dataId}/${imageType}`;
      try {
        await request({
          url: endpoint,
          method: "POST",
          fileBody: file,
        });
        notification.info({
          message: "Image uploaded successfully",
          description: error,
          placement: "bottomRight",
        });
      } catch (e) {
        console.error(e);
      }
      if (refetch) {
        refetch();
      }
    }
  }, [file, refetch, dataId, error, imageFor, imageType, organizationId, request, sport]);

  const { permissions } = useUserPermissions();
  const hasPermissionToEditImage = permissions[permissionKey]?.includes(PermissionOperations.UPDATE) === true;
 
  return (
    <>
      <Wrapper className={`upload ${className}`}>
        {hasPermissionToEditImage ? (
          <UploadZone className="upload-zone">
            {loading || modalLoading ? (
              <Loader/>
            ) : (
              <DropzoneWrapper>
                <SvgWrapper>
                  {base64file || image?.url ? (
                    <img alt={imageType} src={base64file || `${image.url}?size=RAW`}/>
                  ) : (
                    <svg>
                      <line x1="0" y1="100%" x2="100%" y2="0"/>
                      <line x1="0" y1="0" x2="100%" y2="100%"/>
                    </svg>
                  )}
                </SvgWrapper>
                <ImageUploader
                  handleDrop={handleDrop}
                  data={{}}
                  type={"LOGO_BACKGROUND"}
                  element={"entityId"}
                  imageFor={"ENTITY"}
                  newDesign
                />
              </DropzoneWrapper>
            )}
          </UploadZone>
        ) : (
          <SvgWrapper>
            {base64file || image?.url ? (
              <img alt={imageType} src={base64file || `${image.url}?size=RAW`}/>
            ) : (
              <svg>
                <line x1="0" y1="100%" x2="100%" y2="0"/>
                <line x1="0" y1="0" x2="100%" y2="100%"/>
              </svg>
            )}
          </SvgWrapper>
        )}
        <ButtonWrapper className="upload-buttons">
          {!addModalMode && (
            <PermissionCheck permissionKeys={[permissionKey]} permissionOptions={[PermissionOperations.UPDATE]}>
              <Button onClick={handleLoad}>Upload</Button>
            </PermissionCheck>
          )}
          <PermissionCheck permissionKeys={[permissionKey]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Button onClick={handleClear}>Clear</Button>
          </PermissionCheck>
        </ButtonWrapper>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
    display: flex;
    background-color: #ffffff;
    padding: 10px;
    flex-direction: column;
    max-width: 310px;
    height: max-content;
`;
const UploadZone = styled.div`
    width: 290px;
    padding: 5px;
    min-height: 300px;
`;
const ButtonWrapper = styled.div`
    margin-top: 5px;
    display: flex;
    gap: 5px;
`;

const DropzoneWrapper = styled.div`
    top: 0;
    width: 100%;
    height: 100%;

    section {
        margin-top: 2px;
        margin-right: 0;

        p {
            max-width: 100%;
        }

        div {
            width: 100%;
            height: 100%;
        }
    }
`;

const SvgWrapper = styled.div`
    position: relative;
    width: 100%;
    background-color: #F3F3F3;
    height: 290px;
    display: flex;

    img {
        width: 280px;
        margin: auto;
    }

    svg {
        width: 280px;
        height: 280px;

        line {
            stroke: #707070;
            stroke-width: 1;
        }
    }
`;

export default UploadForm;
